import React, { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import styled from 'styled-components';
import { useMutation } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';
import { ScreenLayout, AuthHeader, Spinner } from '../../shared';
import { resetFlowAtom } from '../../../store';

import chatSuccessIcon from 'images/chat-success-icon.png';

function SuccessIllustration() {
  return <img src={chatSuccessIcon} width={120} height={82} alt="Success" />;
}

function PeopleIcon() {
  return (
    <svg
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"
        stroke="#232323"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle
        cx="9"
        cy="7"
        r="4"
        stroke="#232323"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M22 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75"
        stroke="#232323"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function SmsIcon() {
  return (
    <svg
      width="22"
      height="16"
      viewBox="0 0 22 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M18.2886 15.654C18.1768 15.654 18.0662 15.6153 17.9775 15.5402L15.3756 13.3488H2.01495C0.903876 13.3488 0 12.4449 0 11.3338V2.01495C0 0.903876 0.903876 0 2.01495 0H19.7189C20.83 0 21.7338 0.903876 21.7338 2.01495V11.3341C21.7338 12.4451 20.83 13.349 19.7189 13.349H18.7718V15.1713C18.7718 15.3591 18.6629 15.5299 18.4926 15.6091C18.4274 15.6393 18.3579 15.654 18.2886 15.654ZM2.01495 0.965937C1.43659 0.965937 0.965937 1.43659 0.965937 2.01495V11.3341C0.965937 11.9127 1.43659 12.3831 2.01495 12.3831H15.5521C15.6661 12.3831 15.7762 12.4234 15.8631 12.4968L17.8059 14.1329V12.866C17.8059 12.5994 18.0222 12.3831 18.2888 12.3831H19.7189C20.2975 12.3831 20.7679 11.9127 20.7679 11.3341V2.01495C20.7679 1.43659 20.2975 0.965937 19.7189 0.965937H2.01495Z"
        fill="#232323"
      />
    </svg>
  );
}

function ReplySpeedIcon() {
  return (
    <svg
      width="18"
      height="18"
      viewBox="0 0 18 18"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path
        d="M6.75 13.125H2.625M4.875 9H1.5M6.75 4.875H3M12.75 2.25L7.80267 9.17626C7.5837 9.48282 7.47421 9.6361 7.47895 9.7639C7.48308 9.87516 7.53643 9.97884 7.62457 10.0469C7.72581 10.125 7.91418 10.125 8.29091 10.125H12L11.25 15.75L16.1973 8.82374C16.4163 8.51718 16.5258 8.3639 16.521 8.2361C16.5169 8.12484 16.4636 8.02116 16.3754 7.95314C16.2742 7.875 16.0858 7.875 15.7091 7.875H12L12.75 2.25Z"
        stroke="#232323"
        strokeWidth="1.2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const StyledBody = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex-grow: 1;
  padding: 1rem;
  overflow-y: auto;

  &::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const StyledIllustration = styled.div`
  margin-bottom: 0.75rem;
`;

const StyledTitle = styled.h1`
  margin: 0 0 0.5rem;
  font-size: 1.25rem;
  font-weight: 600;
  color: #232323;
  text-align: center;
`;

const StyledSubtitle = styled.p`
  margin: 0 0 1.5rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #232323;
  text-align: center;
`;

const StyledCard = styled.div`
  width: 100%;
  border: 1px solid #d7d3d0;
  border-radius: 0.625rem;
  padding: 1.25rem;
  background: #ffffff;

  & + & {
    margin-top: 0.75rem;
  }
`;

/**
 * TODO: speak to Product
 * This will be temporarily hidden until we are clear on what feedback mechanism is.
 * */
const HiddenStyledCard = styled(StyledCard)`
  display: none;
`;

const StyledCardTitle = styled.h2`
  margin: 0 0 0.75rem;
  font-size: 1rem;
  font-weight: 700;
  color: #232323;
`;

const StyledInfoList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
`;

const StyledInfoItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  font-size: 0.9375rem;
  line-height: 1.5;
  color: #232323;

  svg {
    flex-shrink: 0;
    width: 1.5rem;
    margin-top: 0.125rem;
  }
`;

const StyledCardDescription = styled.p`
  margin: 0 0 1rem;
  font-size: 0.9375rem;
  color: #232323;
  line-height: 1.5;
`;

const StyledFeedbackButton = styled.button`
  width: 100%;
  padding: 0.875rem;
  font-size: 1rem;
  font-weight: 600;
  font-family: inherit;
  color: var(--rg-primary-color, #165e5f);
  background: transparent;
  border: 1.5px solid var(--rg-primary-color, #165e5f);
  border-radius: 0.75rem;
  cursor: pointer;
  transition:
    background 0.2s ease,
    transform 0.15s ease;

  &:hover {
    background: #f0fafa;
  }

  &:active {
    transform: scale(0.98);
  }
`;

export function SuccessPage() {
  const navigate = useNavigate();
  const resetFlow = useSetAtom(resetFlowAtom);

  useEffect(() => {
    resetFlow();
  }, [resetFlow]);

  const listing = window.xprops.listing;
  const communityName = listing?.name ?? '{{community_name}}';

  const handleBack = useCallback(() => {
    navigate('/');
  }, [navigate]);

  const handleClose = useCallback(() => {
    window.xprops?.onToggleMessenger?.();
  }, []);

  const sendFeedback = useMutation({
    mutationFn: async () => {
      await new Promise((resolve) => setTimeout(resolve, 1500));
    },
  });

  const handleFeedback = useCallback(() => {
    sendFeedback.mutate();
  }, [sendFeedback]);

  return (
    <ScreenLayout
      header={<AuthHeader onBack={handleBack} onClose={handleClose} />}
    >
      <StyledBody>
        <StyledIllustration>
          <SuccessIllustration />
        </StyledIllustration>
        <StyledTitle>Message sent!</StyledTitle>
        <StyledSubtitle>
          We delivered your message to the residents at {communityName}.
        </StyledSubtitle>

        <StyledCard>
          <StyledCardTitle>What&rsquo;s next?</StyledCardTitle>
          <StyledInfoList>
            <StyledInfoItem>
              <PeopleIcon />
              <span>
                We sent your message to a few residents for the quickest
                response.
              </span>
            </StyledInfoItem>
            <StyledInfoItem>
              <SmsIcon />
              <span>Replies will be texted to your phone.</span>
            </StyledInfoItem>
            <StyledInfoItem>
              <ReplySpeedIcon />
              <span>Most residents reply within 24 hours.</span>
            </StyledInfoItem>
          </StyledInfoList>
        </StyledCard>

        <HiddenStyledCard>
          <StyledCardTitle>Got a sec?</StyledCardTitle>
          <StyledCardDescription>
            Tell us how your experience went.
          </StyledCardDescription>
          <StyledFeedbackButton
            type="button"
            onClick={handleFeedback}
            disabled={sendFeedback.isLoading || sendFeedback.isSuccess}
          >
            {sendFeedback.isLoading && (
              <Spinner size="1.25rem" color="currentColor" />
            )}
            {sendFeedback.isSuccess && 'Thanks for your feedback!'}
            {!sendFeedback.isLoading &&
              !sendFeedback.isSuccess &&
              'Give feedback'}
          </StyledFeedbackButton>
        </HiddenStyledCard>
      </StyledBody>
    </ScreenLayout>
  );
}
